import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { UUID } from 'angular2-uuid';
import moment from 'moment';
import { EMPTY, Observable, of, throwError, TimeoutError, timer, Subject } from 'rxjs';
import { catchError, map, retryWhen, switchMap, timeout } from 'rxjs/operators';
import { ApiResponse, QueryParams, RequestOptions, ResponseOptions } from '../types/http.types';
import { ExtraParamsOptions, TableListOptions } from '../types/table-list.types';
import { CustomURLEncoder } from './urlEncoder';
import { environment } from '../../../environments/environment';
import { NbGlobalPhysicalPosition, NbToastrService } from '@nebular/theme';
import { Router } from '@angular/router';

@Injectable()
export class HttpService {
  clientRequestId: string = UUID.UUID();
  clientSessionId: string = UUID.UUID();
  REQUEST_TIMEOUT: any;
  REQUEST_TIMEOUT_RETRIES: any;
  REQUEST_RETRY_TIMEOUT: any;
  DEFAULT_PAGE_SIZE: any;
  API_DATE_FORMAT: any;
  unauthorizedRequestObs = new Subject<boolean>();

  constructor(
    private http: HttpClient,
    private toastrService: NbToastrService,
    private router: Router
  ) {
    this.REQUEST_TIMEOUT = environment.mexExchange.api.request.timeout;
    this.REQUEST_TIMEOUT_RETRIES = environment.mexExchange.api.request.timeoutRetries;
    this.REQUEST_RETRY_TIMEOUT = environment.mexExchange.api.request.retryTimeout;
    this.DEFAULT_PAGE_SIZE = environment.mexExchange.api.pageSize;
    this.API_DATE_FORMAT = environment.mexExchange.api.dateFormat;
  }

  private static isTimeoutError(error: any): error is TimeoutError {
    // This does not work, it worked for the previous version of rxjs (5.4)
    // return error instanceof TimeoutError;
    return error instanceof Error && error.name === 'TimeoutError';
  }

  resetRequestId() {
    this.clientRequestId = UUID.UUID();
  }

  resetSessionId() {
    this.clientSessionId = UUID.UUID();
  }

  getHeaders(): HttpHeaders {
    this.resetRequestId();

    const headers = new HttpHeaders({
      'X-Client-Request-Id': this.clientRequestId,
      'X-Client-Session-Id': this.clientSessionId,
    });

    return headers;
  }

  getSearchParams(params: QueryParams = {}): HttpParams {
    let searchParams = new HttpParams({ encoder: new CustomURLEncoder() });

    Object.keys(params).forEach((key) => {
      searchParams = searchParams.set(key, params[key]);
    });

    return searchParams;
  }

  getOptions(headers: HttpHeaders, params?: HttpParams): RequestOptions {
    const requestOptions: RequestOptions = {
      headers,
      params
    };

    return requestOptions;
  }

  getListingParams(
    tableListOptions: TableListOptions = {},
    extraOptions?: ExtraParamsOptions
  ): QueryParams {
    const queryParams: QueryParams = { };

    // old sort
    if (tableListOptions.sort) {
      queryParams.sort = tableListOptions.sort;
    }

    if (tableListOptions.startDate && tableListOptions.endDate) {
      queryParams.startDate = moment
        .utc(tableListOptions.startDate)
        .format(extraOptions && extraOptions.dateFormat ? extraOptions.dateFormat : this.API_DATE_FORMAT);
      queryParams.endDate = moment
        .utc(tableListOptions.endDate)
        .format(extraOptions && extraOptions.dateFormat ? extraOptions.dateFormat : this.API_DATE_FORMAT);
    }

    if (tableListOptions.size) {
      queryParams.size = tableListOptions.size;
    }

    queryParams.page = tableListOptions.page || 0;

    return queryParams;
  }

  setDefaultDateParams(tableListOptions: TableListOptions = {}) {
    tableListOptions.startDate = moment().year(2024).startOf('year');
    tableListOptions.endDate = moment();
  }

  get(url: string, requestOptions: RequestOptions, responseOptions: ResponseOptions = {}, handleError?: boolean): Observable<any> {
    const request = this.http.get<ApiResponse>(url, requestOptions);

    return this.handleRequest(request, responseOptions, handleError, true);
  }

  post(url: string, body: any, requestOptions: RequestOptions, responseOptions: ResponseOptions = {}): Observable<any> {
    const request = this.http.post<ApiResponse>(url, body, requestOptions);

    return this.handleRequest(request, responseOptions);
  }

  postNoResponse(url: string, body: any, requestOptions: RequestOptions): Observable<any> {
    const request = this.http.post<ApiResponse>(url, body, requestOptions);
    return request;
  }

  put(url: string, body: any, requestOptions: RequestOptions, responseOptions: ResponseOptions = {}): Observable<any> {
    const request = this.http.put<ApiResponse>(url, body, requestOptions);

    return this.handleRequest(request, responseOptions);
  }

  delete(url: string, requestOptions: RequestOptions, responseOptions: ResponseOptions = {}, body: any = {}): Observable<any> {
    if (Object.keys(body).length) {
      requestOptions.body = body;
    }
    const request = this.http.delete<ApiResponse>(url, requestOptions);

    return this.handleRequest(request, responseOptions);
  }

  private handleRequest(request: Observable<ApiResponse>, responseOptions: ResponseOptions, handleError: boolean = true, retry: boolean = false): Observable<any> {
    const fullBody = responseOptions.fullBody || false;
    let retriesLeft = this.REQUEST_TIMEOUT_RETRIES;
    const handleErrorCallback = handleError
      ? catchError(this.handleError.bind(this))
      : catchError((err) => {
          return EMPTY;
        });

    return request.pipe(
      // Throws an error after the specified timeout
      timeout(responseOptions.timeout || this.REQUEST_TIMEOUT),
      // Only retry when the error was a timeout and there are retries left on GET calls
      retryWhen((errors) =>
        errors.pipe(
          switchMap((sourceError) => {
            if (retry && HttpService.isTimeoutError(sourceError) && retriesLeft > 0) {
              retriesLeft--;
              // Attempt request again after timeout
              return timer(this.REQUEST_RETRY_TIMEOUT).pipe(switchMap(() => of(true)));
            } else {
              // Re-throw original error
              return throwError(sourceError);
            }
          })
        )
      ),
      map(this.extractResponse(fullBody)),
      handleErrorCallback
    );
  }

  private extractResponse(fullBody: boolean) {
    return (data: ApiResponse) => {
      const body = data;

      if (fullBody) {
        return body;
      } else {
        return (body && body.data) || {};
      }
    };
  }

  private handleError(error: any) {
    // We need to parse the error for those endpoints where we set responseType = "text"
    if (error.error && typeof error.error === 'string') {
      error.error = JSON.parse(error.error);
    }
    let errorTitle: string = error.error?.errorMessage;
    let errorMessage: string = error.error?.details;
    if (!errorTitle) {
      switch (error.status) {
        case 401:
        case 403: {
          errorMessage = 'You do not have permissions to see this resource.';
          break;
        }
        case 404: {
          errorMessage = 'The requested element could not be found.';
          break;
        }
        case 500: {
          errorMessage = 'An unexpected error occurred with your request.';
          break;
        }
        default:
          errorMessage = 'An unexpected error occurred with your request.';
          break;
      }
    }

    if (errorMessage === 'Missing permission') {
      const previousTableParams = localStorage.getItem('previousTableParams');
      // We suppose that the user is trying to access an entity by ID without the needed permissions; e.g. '/pages/risk/credit-line/29'
      const lastSlashIndex = location.pathname.lastIndexOf('/');
      const path = location.pathname.slice(0, lastSlashIndex);
      this.router.navigate([path], {
        queryParams: previousTableParams ? JSON.parse(previousTableParams) : {},
      });
      setTimeout(() => {
        this.notifyError(errorMessage, errorTitle || 'Error');
        return throwError(error);
      }, 500);
    } else {
      this.notifyError(errorMessage, errorTitle || 'Error');
      return throwError(error);
    }
  }

  private notifyError(errorMessage: string, errorTitle: string) {
    this.toastrService.show(errorMessage, errorTitle,
      { position: NbGlobalPhysicalPosition.TOP_RIGHT, status: 'danger', duration: 12000 , icon: 'close-outline' });
  }
}
